'use client';

/**
 * The Ads admin tab (ads build plan section 2.5): the gate, the house ads,
 * their counts, the reports and a live preview of the slot.
 *
 * Copies the Attribution tab's shape (a readout, a form, audited writes with
 * a note) and the deck tab's CRUD table. Access is enforced by the routes
 * (staff read, admin write); this component renders the 403 as a message and
 * disables the forms when `canWrite` is false.
 *
 * The gate is written as ONE object (`PUT /api/admin/ads`), so the audit row
 * shows the whole before and after; the house list is written whole too, by
 * the routes, one ad at a time from here. Labels are admin English like the
 * other tabs; everything a person sees in the slot itself comes from
 * en.json (`ads.*`) inside the AdSlot component, which the Preview section
 * mounts in `preview` mode at the three widths the plan names.
 *
 * LANE E (plan section 5): the Sources section becomes connectable rows with
 * Mediation and ads.txt beside them (components/admin/ads-sources-panel.tsx),
 * saved by the same Save as the gate (`PUT /api/admin/ads` with `sources`);
 * the Compare section (components/admin/ads-compare-panel.tsx) sits above the
 * house ads; the house form gains the conformance fields (advertiser
 * domains, Ad Product categories; headline 25, paid-by 100) and the table
 * flags a stored headline that no longer fits and counts impressions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ImagePlus, Megaphone, Pencil, Plus, RefreshCw, Save, ShieldAlert, Trash2, X } from 'lucide-react';

import { AD_PICTURE_HEIGHT_APP_BAR, AD_PICTURE_HEIGHT_TITLE_BAR, AdSlot } from '@/components/ads/ad-slot';
import { AdsComparePanel } from '@/components/admin/ads-compare-panel';
import { CTR_LABEL, CTR_TITLE, ctrText } from '@/components/admin/ads-metrics';
import { AdsSourcesPanel, type SourceReadoutRow } from '@/components/admin/ads-sources-panel';
import { Spinner } from '@/components/ui/spinner';
import { AD_PICTURE_LIMITS, adUrlProblem, defaultAdomain, HOUSE_AD_LIMITS, houseAdConformanceProblem, isDefaultAdomain } from '@/lib/ads/config';
import { AD_IDLE_LIMITS, AD_REFRESH_LIMITS, isValidIdleSec, isValidRefreshSec } from '@/lib/ads/refresh';
import type { AdsSourcesSettings } from '@/lib/ads/source-settings';
import {
  AD_PLACEMENTS,
  canonicalAdSource,
  type AdPicture,
  type AdPlacement,
  type AdsConfig,
  type AdSourceId,
  type HouseAd,
  type ServedAd,
} from '@/lib/ads/types';
import { cn } from '@/lib/utils';

interface ListedUser {
  id: string;
  username: string;
  displayName: string;
}

/** A house ad as the routes read it out: the stored record plus a signed URL of its picture, when it has one. */
type HouseAdRow = HouseAd & { pictureUrl?: string };

/** The banner over the house form (the picture round, 2026-09-30). */
export const HOUSE_FORM_RULES = 'No political ads. Text and one small picture, no animation. The link must be https.';

interface Readout {
  canWrite: boolean;
  config: AdsConfig;
  source: string;
  envForced: boolean;
  users: ListedUser[];
  sources: SourceReadoutRow[];
  sourceSettings: AdsSourcesSettings;
  adsTxt: string;
  placements: Array<{ id: AdPlacement; built: boolean }>;
}

interface Totals {
  served: number;
  viewable: number;
  clicks: number;
  impressions?: number;
  /** Serves that answered a refresh (the refresh round, 2026-09-30). */
  refreshed?: number;
}

interface StatsReadout {
  days: number;
  totals: { d7: Record<string, Totals>; d30: Record<string, Totals> };
}

interface Report {
  id: string;
  adId: string;
  reason: string;
  placement: string | null;
  appId: string | null;
  userId: string | null;
  createdAt: string;
  source?: string;
  advertiser?: string;
  headline?: string;
}

const PLACEMENT_LABELS: Record<AdPlacement, string> = {
  titleBar: 'Window title bar (desktop)',
  appBar: 'App bar (phone app view and phone full screen)',
  fullScreen: 'Full-screen bar (desktop)',
  // The apex share page draws the same bar as a named host since 2026-10-02
  // (the owner's "/w fullscreen ui should be the same as the green button's"),
  // and its slot is this placement too: one page, one switch.
  appAddress: "The app's own address and share links",
};

const REASON_LABELS: Record<string, string> = {
  misleading: 'Misleading',
  offensive: 'Offensive',
  not_relevant: 'Not relevant',
  other: 'Other',
};

const EMPTY_TOTALS: Totals = { served: 0, viewable: 0, clicks: 0 };

/**
 * The house table's CTR: clicks over impressions, the industry's definition
 * and the one Compare uses, under the same label and tooltip (fix round 2,
 * 2026-09-30, components/admin/ads-metrics.ts). It was clicks over viewable
 * impressions (over served while nothing was viewable), under the same
 * "CTR" heading as Compare's clicks over impressions.
 */
function ctr(t: Totals): string {
  return ctrText(t.clicks, t.impressions ?? 0);
}

// One 32 px control height for inputs, selects and buttons (the design pass of
// 2026-09-30 measured 33, 34 and 32 px side by side).
const inputClass = 'h-8 w-full rounded-md border border-border bg-background px-2 text-sm';
const buttonClass = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 text-sm hover:bg-muted disabled:opacity-50';
const primaryButtonClass = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50';

async function readError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return body?.error ?? `${fallback} (${res.status})`;
}

export function AdsTab() {
  const [readout, setReadout] = useState<Readout | null>(null);
  const [draft, setDraft] = useState<AdsConfig | null>(null);
  const [users, setUsers] = useState<ListedUser[]>([]);
  const [ads, setAds] = useState<HouseAdRow[]>([]);
  const [stats, setStats] = useState<StatsReadout | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [lookup, setLookup] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [excludeText, setExcludeText] = useState('');
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [sourcesDraft, setSourcesDraft] = useState<AdsSourcesSettings | null>(null);
  // Bumped whenever the saved settings are loaded again, so the panel's typed text starts over from them.
  const [sourcesVersion, setSourcesVersion] = useState(0);
  const [compareKey, setCompareKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [gate, house, statsRes, reportsRes] = await Promise.all([
        fetch('/api/admin/ads', { credentials: 'same-origin' }),
        fetch('/api/admin/ads/house', { credentials: 'same-origin' }),
        fetch('/api/admin/ads/stats?days=30', { credentials: 'same-origin' }),
        fetch('/api/admin/ads/reports', { credentials: 'same-origin' }),
      ]);
      if (gate.status === 403) {
        setForbidden(true);
        return;
      }
      if (!gate.ok) throw new Error(await readError(gate, 'Request failed'));
      const body = (await gate.json()) as Readout;
      setReadout(body);
      setDraft(body.config);
      setUsers(body.users);
      setExcludeText(body.config.excludeCategories.join(', '));
      setSourcesDraft(body.sourceSettings);
      setSourcesVersion((v) => v + 1);
      setCompareKey((k) => k + 1);
      if (house.ok) setAds(((await house.json()) as { ads: HouseAdRow[] }).ads ?? []);
      if (statsRes.ok) setStats((await statsRes.json()) as StatsReadout);
      if (reportsRes.ok) setReports(((await reportsRes.json()) as { reports: Report[] }).reports ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canWrite = readout?.canWrite ?? false;

  const saveGate = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const excludeCategories = excludeText
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const config: AdsConfig = { ...draft, userIds: users.map((u) => u.id), excludeCategories };
      const res = await fetch('/api/admin/ads', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ config, ...(sourcesDraft ? { sources: sourcesDraft } : {}), note: note.trim() || undefined }),
      });
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      if (!res.ok) throw new Error(await readError(res, 'Save failed'));
      const body = (await res.json()) as Readout;
      setReadout(body);
      setDraft(body.config);
      setUsers(body.users);
      setExcludeText(body.config.excludeCategories.join(', '));
      setSourcesDraft(body.sourceSettings);
      setSourcesVersion((v) => v + 1);
      setNote('');
      setNotice('Saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [draft, users, excludeText, note, sourcesDraft]);

  const toggleSource = useCallback(
    (id: AdSourceId, on: boolean) => {
      if (!draft) return;
      const rest = draft.sources.filter((s) => canonicalAdSource(s) !== id);
      setDraft({ ...draft, sources: on ? [...rest, id] : rest });
    },
    [draft],
  );

  const addPerson = useCallback(async () => {
    const q = lookup.trim();
    if (!q) return;
    setLookupBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/ads/lookup?q=${encodeURIComponent(q)}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(await readError(res, 'Lookup failed'));
      const body = (await res.json()) as { user: ListedUser | null };
      if (!body.user) {
        setError(`No account matches "${q}". Enter an exact username or email.`);
        return;
      }
      const found = body.user;
      setUsers((list) => (list.some((u) => u.id === found.id) ? list : [...list, found]));
      setLookup('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLookupBusy(false);
    }
  }, [lookup]);

  const submitAd = useCallback(
    async (id: string | null, input: Record<string, unknown>) => {
      setSaving(true);
      setError(null);
      setNotice(null);
      try {
        const res = await fetch(id ? `/api/admin/ads/house/${encodeURIComponent(id)}` : '/api/admin/ads/house', {
          method: id ? 'PATCH' : 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ad: input }),
        });
        if (res.status === 403) {
          setForbidden(true);
          return;
        }
        if (!res.ok) throw new Error(await readError(res, 'Save failed'));
        const body = (await res.json()) as { ads: HouseAdRow[] };
        setAds(body.ads ?? []);
        setEditing(null);
        setNotice(id ? 'Ad updated.' : 'Ad added.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const deleteAd = useCallback(async (ad: HouseAd) => {
    if (!confirm(`Delete the ad from ${ad.advertiser} ("${ad.headline}")? Its counts stay in the stats.`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/ads/house/${encodeURIComponent(ad.id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await readError(res, 'Delete failed'));
      const body = (await res.json()) as { ads: HouseAdRow[] };
      setAds(body.ads ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  }, []);

  const previewAd = useMemo<ServedAd>(() => {
    const first = ads.find((a) => a.enabled) ?? ads[0];
    if (first) {
      return {
        id: first.id,
        source: 'house',
        advertiser: first.advertiser,
        headline: first.headline,
        description: first.description,
        displayHost: first.displayHost,
        paidBy: first.paidBy,
        category: first.categories[0] ?? null,
        ...(first.picture && first.pictureUrl ? { image: { url: first.pictureUrl, width: first.picture.width, height: first.picture.height } } : {}),
      };
    }
    return {
      id: 'preview',
      source: 'house',
      advertiser: 'Example Co',
      headline: 'Ship your app this week',
      description: 'A short line about the offer.',
      displayHost: 'example.com',
      paidBy: 'Example Co',
      category: 'productivity',
    };
  }, [ads]);

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Admin access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">You need a staff account to view the ads settings.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="flex items-center gap-2">
        <Megaphone className="h-5 w-5" />
        <h1 className="text-xl font-semibold">Ads</h1>
        <button type="button" onClick={() => void load()} disabled={loading} className={cn(buttonClass, 'ml-auto')}>
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>
      <p className="text-sm text-muted-foreground">
        One line of text in the app chrome, chosen by the app being shown, never by the person. Off by default. Every change here is
        audited.
      </p>

      {error ? <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div> : null}
      {readout?.envForced ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          ADS_ENABLED=false is set in this environment, so nobody sees ads whatever is saved here. The setting reads as env-force.
        </div>
      ) : null}

      {loading && !readout ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : null}

      {readout && draft ? (
        <>
          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Who sees ads</h2>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {(['nobody', 'everyone', 'list'] as const).map((a) => (
                <label key={a} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="ads-audience"
                    checked={draft.audience === a}
                    disabled={!canWrite}
                    onChange={() => setDraft({ ...draft, audience: a })}
                  />
                  {a === 'nobody' ? 'Nobody' : a === 'everyone' ? 'Everyone' : 'Only these people'}
                </label>
              ))}
            </div>
            {draft.audience === 'list' ? (
              <div className="mt-3 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {users.length === 0 ? <span className="text-xs text-muted-foreground">Nobody listed yet.</span> : null}
                  {users.map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">
                      {u.username ? `@${u.username}` : u.id.slice(0, 8)}
                      {canWrite ? (
                        <button
                          type="button"
                          aria-label={`Remove ${u.username || u.id}`}
                          onClick={() => setUsers((list) => list.filter((x) => x.id !== u.id))}
                          className="rounded p-0.5 hover:bg-accent"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      ) : null}
                    </span>
                  ))}
                </div>
                {canWrite ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={lookup}
                      onChange={(e) => setLookup(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void addPerson();
                        }
                      }}
                      placeholder="Exact username or email"
                      className={cn(inputClass, 'max-w-xs')}
                    />
                    <button type="button" onClick={() => void addPerson()} disabled={lookupBusy || !lookup.trim()} className={buttonClass}>
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </button>
                    <span className="text-xs text-muted-foreground">Stored as ids; at most 500.</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Which apps</h2>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-1.5">
                <input type="radio" name="ads-apps" checked={draft.apps === 'listed'} disabled={!canWrite} onChange={() => setDraft({ ...draft, apps: 'listed' })} />
                Listed apps only (in the public catalog)
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" name="ads-apps" checked={draft.apps === 'all'} disabled={!canWrite} onChange={() => setDraft({ ...draft, apps: 'all' })} />
                All apps
              </label>
            </div>
            <label className="mt-3 block text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">Excluded categories (comma separated; a category excludes its subcategories)</span>
              <input type="text" value={excludeText} disabled={!canWrite} onChange={(e) => setExcludeText(e.target.value)} placeholder="games" className={cn(inputClass, 'max-w-md')} />
            </label>
            <p className="mt-1 text-xs text-muted-foreground">Chat windows and call apps never carry an ad, whatever is listed here.</p>
          </section>

          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Where</h2>
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              {readout.placements.map((p) => (
                <label key={p.id} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={draft.placements[p.id]}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, placements: { ...draft.placements, [p.id]: e.target.checked } })}
                  />
                  {PLACEMENT_LABELS[p.id]}
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Paying plans</h2>
            <label className="mt-2 flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={draft.hideForPaying} disabled={!canWrite} onChange={(e) => setDraft({ ...draft, hideForPaying: e.target.checked })} />
              Hide ads for paying plans
            </label>
          </section>

          <RefreshSection
            value={draft.refreshSec}
            idleValue={draft.idleSec}
            canWrite={canWrite}
            onChange={(refreshSec) => setDraft({ ...draft, refreshSec })}
            onIdleChange={(idleSec) => setDraft({ ...draft, idleSec })}
          />

          {sourcesDraft ? (
            <AdsSourcesPanel
              key={sourcesVersion}
              rows={readout.sources}
              enabled={draft.sources}
              onToggle={toggleSource}
              settings={sourcesDraft}
              onChange={setSourcesDraft}
              canWrite={canWrite}
            />
          ) : null}

          {canWrite ? (
            // Sticky at the bottom of the dialog while the gate's sections are
            // being edited above it: it sat about 3,450 px down, and "Saved."
            // appeared at the top of the page, out of view (the design pass
            // of 2026-09-30). The status now shows here, beside the button.
            // It sticks to the DIALOG's scroller, which needs every box in
            // between to create no scroll container of its own: the admin
            // view's content box is `overflow-x-clip` for that reason. Its
            // edges line up with the sections above it (no negative margin).
            <div
              data-testid="ads-save-bar"
              className="sticky bottom-0 z-10 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-background/95 px-3 py-3 shadow-[0_-6px_16px_-12px_rgba(0,0,0,0.35)] backdrop-blur"
            >
              <label className="min-w-0 flex-1 text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">Note (optional, goes in the audit trail)</span>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this change" maxLength={500} className={inputClass} />
              </label>
              {notice ? (
                <span role="status" className="flex h-8 items-center text-sm text-emerald-600 dark:text-emerald-400">
                  {notice}
                </span>
              ) : null}
              <button type="button" onClick={() => void saveGate()} disabled={saving} className={primaryButtonClass}>
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save gate'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Read only: an admin account is needed to change the gate or the ads.</p>
          )}

          <AdsComparePanel refreshKey={compareKey} />

          <section className="rounded-lg border border-border">
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">House ads</h2>
              <span className="text-xs text-muted-foreground">
                {ads.length} of {HOUSE_AD_LIMITS.maxAds}
              </span>
              {notice ? (
                <span role="status" className="text-xs text-emerald-600 dark:text-emerald-400">
                  {notice}
                </span>
              ) : null}
              {canWrite && editing === null ? (
                <button type="button" onClick={() => setEditing('new')} className={cn(buttonClass, 'ml-auto')}>
                  <Plus className="h-3.5 w-3.5" />
                  New ad
                </button>
              ) : null}
            </div>
            {editing === 'new' ? (
              <div className="border-b border-border p-4">
                <HouseAdForm busy={saving} onCancel={() => setEditing(null)} onSubmit={(input) => void submitAd(null, input)} />
              </div>
            ) : null}
            <p className="px-4 pb-2 pt-3 text-xs text-muted-foreground">Each count is the last 7 days / the last 30 days.</p>
            {/* The whole row fits the dialog with no sideways scroll: at the
                dialog's 936 px the eleven one-line columns came to about
                1,120 px, so the click-through rate, the switch and the edit
                buttons sat off screen behind a scrollbar (the design pass of
                2026-09-30, Z10). The link host now sits under the advertiser
                instead of in a column of its own, the three long count
                headers use the short names of the Compare table above (the
                full name is the tooltip), and the headline takes what is
                left and truncates. The scroll wrapper stays for a narrow
                dialog, where the headline's floor makes the table wider. */}
            <div className="overflow-x-auto" data-testid="house-ads-scroll">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">Advertiser</th>
                    <th className="w-full min-w-[9rem] max-w-0 whitespace-nowrap px-3 py-2 font-medium">Headline</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title="Served: times the ad was sent to a slot">
                      Served
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title="Refreshes: of the serves, how many answered a refresh (a timed rotation, or a navigation after the ad was in view)">
                      Refr
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title="Impressions: counted when the ad was actually drawn">
                      Impr
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title="Viewable impressions">
                      View
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title="Clicks, counted at our redirect">
                      Clicks
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium" title={CTR_TITLE}>
                      {CTR_LABEL}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">On</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {ads.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">
                        No house ads yet.
                      </td>
                    </tr>
                  ) : (
                    ads.map((ad) => {
                      const d7 = stats?.totals.d7[ad.id] ?? EMPTY_TOTALS;
                      const d30 = stats?.totals.d30[ad.id] ?? EMPTY_TOTALS;
                      const isEditing = editing === ad.id;
                      return (
                        <AdRow
                          key={ad.id}
                          ad={ad}
                          d7={d7}
                          d30={d30}
                          canWrite={canWrite}
                          busy={saving}
                          editing={isEditing}
                          onEdit={() => setEditing(ad.id)}
                          onCancel={() => setEditing(null)}
                          onSubmit={(input) => void submitAd(ad.id, input)}
                          onToggle={() => void submitAd(ad.id, { enabled: !ad.enabled })}
                          onDelete={() => void deleteAd(ad)}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-border">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Reports</h2>
            </div>
            {reports.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">No reports.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">Ad</th>
                      <th className="px-3 py-2 font-medium">Reason</th>
                      <th className="px-3 py-2 font-medium">Placement</th>
                      <th className="px-3 py-2 font-medium">Reporter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => {
                      const ad = ads.find((a) => a.id === r.adId);
                      return (
                        <tr key={r.id} className="border-t border-border">
                          <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleString()}</td>
                          <td className="px-3 py-2">
                            {ad ? (
                              `${ad.advertiser}: ${ad.headline}`
                            ) : r.advertiser ? (
                              `${r.advertiser}: ${r.headline ?? ''} (${r.source ?? 'network'})`
                            ) : (
                              <span className="font-mono text-xs">
                                {r.adId.slice(0, 8)} ({r.source && r.source !== 'house' ? r.source : 'deleted'})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">{REASON_LABELS[r.reason] ?? r.reason}</td>
                          <td className="px-3 py-2 text-xs">{r.placement ?? ''}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.userId ? r.userId.slice(0, 8) : 'anonymous'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="min-w-0 rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Preview</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              The real slot in preview mode, at 360, 448 and 1040 px of free middle. Nothing is fetched or counted here; the link is inert.
            </p>
            <div className="mt-3 min-w-0 space-y-3">
              {[360, 448, 1040].map((w) => (
                <div key={w} className="max-w-full overflow-x-auto">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{w} px</div>
                  <div className="mt-1 inline-flex items-center gap-2 rounded-xl border border-border bg-muted/30 py-2 pl-3 pr-3">
                    <div className="min-w-0 truncate text-[13px] font-semibold">App</div>
                    {/* The slot fills exactly this box, so the width named above is the free middle it measures. */}
                    <div className="flex shrink-0" style={{ width: w }}>
                      <AdSlot placement="titleBar" appId={null} active preview={previewAd} />
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <span className="h-5 w-5 rounded-full bg-emerald-400/90" />
                      <span className="h-5 w-5 rounded-full bg-amber-400/90" />
                      <span className="h-5 w-5 rounded-full bg-rose-400/90" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function AdRow({
  ad,
  d7,
  d30,
  canWrite,
  busy,
  editing,
  onEdit,
  onCancel,
  onSubmit,
  onToggle,
  onDelete,
}: {
  ad: HouseAdRow;
  d7: Totals;
  d30: Totals;
  canWrite: boolean;
  busy: boolean;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSubmit: (input: Record<string, unknown>) => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <tr className={cn('border-t border-border', !ad.enabled && 'opacity-60')}>
        <td className="whitespace-nowrap px-3 py-2 font-medium">
          <span className="inline-flex items-center gap-2">
            {/* A fixed slot whether or not the ad has a picture, so every
                advertiser's name starts on the same line. */}
            <span className="flex h-6 w-9 shrink-0 items-center justify-center">
              {ad.pictureUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={ad.pictureUrl} alt="" data-testid="house-ad-picture" className="h-6 w-auto max-w-9 rounded-[3px] border border-foreground/20 bg-muted/40 object-contain" />
              ) : null}
            </span>
            {/* The name, and under it where the ad links: one cell, so the
                host costs no column (see the note above the table). */}
            <span className="min-w-0">
              <span className="block max-w-[11rem] truncate" title={ad.advertiser}>
                {ad.advertiser}
              </span>
              <span className="block max-w-[11rem] truncate font-mono text-[11px] font-normal text-muted-foreground" title={`Links to ${ad.displayHost}`} data-testid="house-ad-host">
                {ad.displayHost}
              </span>
            </span>
          </span>
        </td>
        <td className="w-full min-w-[9rem] max-w-0 px-3 py-2">
          <div className="truncate" title={ad.headline}>
            {ad.headline}
          </div>
          {ad.description ? (
            <div className="truncate text-xs text-muted-foreground" title={ad.description}>
              {ad.description}
            </div>
          ) : null}
          {houseAdConformanceProblem(ad) ? (
            <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-600">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {houseAdConformanceProblem(ad)}
            </div>
          ) : null}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {d7.served} / {d30.served}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {d7.refreshed ?? 0} / {d30.refreshed ?? 0}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {d7.impressions ?? 0} / {d30.impressions ?? 0}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {d7.viewable} / {d30.viewable}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {d7.clicks} / {d30.clicks}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
          {ctr(d7)} / {ctr(d30)}
        </td>
        <td className="px-3 py-2">
          <button
            type="button"
            role="switch"
            aria-checked={ad.enabled}
            disabled={!canWrite || busy}
            onClick={onToggle}
            className={cn('rounded-full border px-2 py-0.5 text-xs', ad.enabled ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-border')}
          >
            {ad.enabled ? 'On' : 'Off'}
          </button>
        </td>
        <td className="px-2 py-2">
          {canWrite ? (
            <div className="flex items-center justify-end gap-1">
              <button type="button" onClick={editing ? onCancel : onEdit} disabled={busy} title={editing ? 'Cancel' : 'Edit'} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground">
                {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              </button>
              <button type="button" onClick={onDelete} disabled={busy} title="Delete" className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
        </td>
      </tr>
      {editing ? (
        <tr className="bg-muted/10">
          <td colSpan={10} className="p-4">
            <HouseAdForm initial={ad} busy={busy} onCancel={onCancel} onSubmit={onSubmit} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The refresh interval (the owner's decision, 2026-09-30; lib/ads/refresh.ts):
 * off, or 30 to 600 seconds of VIEWABLE time. The number field keeps what
 * the admin types and reports a value outside the bounds; Save gate sends
 * the draft, which the server validates again.
 */
function RefreshSection({
  value,
  idleValue,
  canWrite,
  onChange,
  onIdleChange,
}: {
  value: number | undefined;
  idleValue: number | undefined;
  canWrite: boolean;
  onChange: (refreshSec: number) => void;
  onIdleChange: (idleSec: number) => void;
}) {
  const current = isValidRefreshSec(value) ? value : AD_REFRESH_LIMITS.defaultSec;
  const off = current === AD_REFRESH_LIMITS.off;
  const [seconds, setSeconds] = useState(String(off ? AD_REFRESH_LIMITS.defaultSec : current));
  useEffect(() => {
    if (!off) setSeconds(String(current));
  }, [current, off]);
  const typed = Number(seconds);
  const problem = !off && !isValidRefreshSec(typed) ? `Between ${AD_REFRESH_LIMITS.minSec} and ${AD_REFRESH_LIMITS.maxSec} seconds.` : null;
  // The idle cooldown (the owner, 2026-09-30: "eye ball time also counts").
  const idleCurrent = isValidIdleSec(idleValue) ? idleValue : AD_IDLE_LIMITS.defaultSec;
  const [idleSeconds, setIdleSeconds] = useState(String(idleCurrent));
  useEffect(() => {
    setIdleSeconds(String(idleCurrent));
  }, [idleCurrent]);
  const idleProblem = !isValidIdleSec(Number(idleSeconds)) ? `Between ${AD_IDLE_LIMITS.minSec} and ${AD_IDLE_LIMITS.maxSec} seconds.` : null;
  return (
    <section className="rounded-lg border border-border p-4" data-testid="ads-refresh-section">
      <h2 className="text-sm font-semibold">Refresh</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        A drawn ad asks for its next one after this much time in view: counted only while the tab is visible and focused, the ad is uncovered and
        at its minimum width, and the person gave input within the cooldown below: mouse, keys or touch on the page, or a click, tap or key press inside an app. Never while the pointer is over it, focus is in it or its panel is open. House ads, Kevel
        and the OpenRTB exchange take part in a timed refresh; EthicalAds and BuySellAds only when the person changes window, screen or tab after
        45 seconds in view.
      </p>
      {/* One grid for both rows: the first column holds each row's label, so
          the two number fields start on the same line (the design pass of
          2026-09-30 measured them 37 px apart). */}
      <div className="mt-3 grid grid-cols-[11rem_6rem_minmax(0,1fr)] items-center gap-x-4 gap-y-2 text-sm" data-testid="ads-refresh-grid">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5">
            <input type="radio" name="ads-refresh" checked={off} disabled={!canWrite} onChange={() => onChange(AD_REFRESH_LIMITS.off)} />
            Off
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="ads-refresh"
              checked={!off}
              disabled={!canWrite}
              onChange={() => onChange(isValidRefreshSec(typed) && typed !== AD_REFRESH_LIMITS.off ? typed : AD_REFRESH_LIMITS.defaultSec)}
            />
            Every
          </label>
        </div>
        <input
          type="number"
          aria-label="Refresh interval in seconds"
          min={AD_REFRESH_LIMITS.minSec}
          max={AD_REFRESH_LIMITS.maxSec}
          step={1}
          value={seconds}
          disabled={!canWrite || off}
          onChange={(e) => {
            setSeconds(e.target.value);
            const n = Number(e.target.value);
            if (isValidRefreshSec(n) && n !== AD_REFRESH_LIMITS.off) onChange(n);
          }}
          className={inputClass}
        />
        <span className="text-xs text-muted-foreground">
          seconds in view ({AD_REFRESH_LIMITS.minSec} to {AD_REFRESH_LIMITS.maxSec}; {AD_REFRESH_LIMITS.defaultSec} by default)
          {problem ? <span className="ml-2 text-destructive">{problem}</span> : null}
        </span>
        <span>Counts as watched for</span>
        <input
          type="number"
          aria-label="Idle cooldown in seconds"
          data-testid="ads-idle-seconds"
          min={AD_IDLE_LIMITS.minSec}
          max={AD_IDLE_LIMITS.maxSec}
          step={1}
          value={idleSeconds}
          disabled={!canWrite}
          onChange={(e) => {
            setIdleSeconds(e.target.value);
            const n = Number(e.target.value);
            if (isValidIdleSec(n)) onIdleChange(n);
          }}
          className={inputClass}
        />
        <span className="text-xs text-muted-foreground">
          seconds after the last input ({AD_IDLE_LIMITS.minSec} to {AD_IDLE_LIMITS.maxSec}; {AD_IDLE_LIMITS.defaultSec} by default)
          {idleProblem ? <span className="ml-2 text-destructive">{idleProblem}</span> : null}
        </span>
      </div>
    </section>
  );
}

/**
 * The house ad's small picture (the owner's decision, 2026-09-30): a file
 * input that accepts PNG, JPEG or WebP, judged by the upload route (no SVG,
 * no GIF, no animation, at most 200 KB, at least 48 x 48), previewed at the
 * two sizes the slot draws (24 px in a title bar, 28 px in an app bar), and
 * saved with the ad as a reference to the stored file.
 */
function PictureField({
  picture,
  pictureUrl,
  onChange,
}: {
  picture: AdPicture | null;
  pictureUrl: string | null;
  onChange: (next: { picture: AdPicture | null; pictureUrl: string | null }) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const upload = async (file: File) => {
    setProblem(null);
    if (file.size > AD_PICTURE_LIMITS.maxBytes) {
      setProblem(`The picture is over ${Math.round(AD_PICTURE_LIMITS.maxBytes / 1024)} KB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/admin/ads/house/picture', { method: 'POST', credentials: 'same-origin', body: form });
      if (!res.ok) throw new Error(await readError(res, 'Upload failed'));
      const body = (await res.json()) as { picture: AdPicture; pictureUrl: string };
      onChange({ picture: body.picture, pictureUrl: body.pictureUrl });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="text-sm sm:col-span-2" data-testid="house-ad-picture-field">
      <span className="mb-1 block text-xs text-muted-foreground">
        Picture (optional): PNG, JPEG or WebP, no animation, at most {Math.round(AD_PICTURE_LIMITS.maxBytes / 1024)} KB, at least {AD_PICTURE_LIMITS.minPx} x{' '}
        {AD_PICTURE_LIMITS.minPx} px. Shown between the Ad label and the text, {AD_PICTURE_HEIGHT_TITLE_BAR} px tall in a title bar and{' '}
        {AD_PICTURE_HEIGHT_APP_BAR} px in an app bar.
      </span>
      <div className="flex flex-wrap items-center gap-3">
        {picture && pictureUrl ? (
          <>
            {[AD_PICTURE_HEIGHT_TITLE_BAR, AD_PICTURE_HEIGHT_APP_BAR].map((h) => (
              <span key={h} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1" title={`${h} px, as drawn`}>
                <span className="rounded-sm bg-foreground/85 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-background">Ad</span>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={pictureUrl}
                  alt=""
                  data-testid={`house-ad-picture-preview-${h}`}
                  className="shrink-0 rounded-[3px] border border-border/70 bg-muted/40 object-contain"
                  style={{ height: h, width: 'auto', maxWidth: Math.round(h * 1.5) }}
                />
                <span className="text-[10px] text-muted-foreground">{h} px</span>
              </span>
            ))}
            <span className="text-xs text-muted-foreground">
              {picture.width} × {picture.height}
            </span>
            <button type="button" onClick={() => onChange({ picture: null, pictureUrl: null })} disabled={busy} className={buttonClass}>
              <X className="h-3.5 w-3.5" />
              Remove picture
            </button>
          </>
        ) : null}
        {/* `relative`: the hidden file input is absolutely placed (`sr-only`),
            and without a positioned label its containing block was the
            dialog's scrolling panel, so focusing it scrolled the whole Admin
            dialog out of view (the design pass of 2026-09-30, BUG01). */}
        <label className={cn(buttonClass, 'relative cursor-pointer', busy && 'opacity-50')}>
          <ImagePlus className="h-3.5 w-3.5" />
          {picture ? 'Replace picture' : 'Add picture'}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={busy}
            className="sr-only"
            data-testid="house-ad-picture-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </label>
        {busy ? <Spinner size="sm" tone="muted" /> : null}
      </div>
      {problem ? <p className="mt-1 text-xs text-destructive">{problem}</p> : null}
    </div>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function HouseAdForm({
  initial,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: HouseAdRow;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: Record<string, unknown>) => void;
}) {
  const [advertiser, setAdvertiser] = useState(initial?.advertiser ?? '');
  const [picture, setPicture] = useState<AdPicture | null>(initial?.picture ?? null);
  const [pictureUrl, setPictureUrl] = useState<string | null>(initial?.pictureUrl ?? null);
  const [headline, setHeadline] = useState(initial?.headline ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [paidBy, setPaidBy] = useState(initial?.paidBy ?? '');
  const [categories, setCategories] = useState((initial?.categories ?? []).join(', '));
  const [placements, setPlacements] = useState<AdPlacement[]>(initial?.placements ?? []);
  const [startsAt, setStartsAt] = useState(toLocalInput(initial?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(initial?.endsAt ?? null));
  const [weight, setWeight] = useState(String(initial?.weight ?? HOUSE_AD_LIMITS.weightDefault));
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  // Empty means "the registrable domain of the link", which then follows the link.
  const [adomain, setAdomain] = useState(initial && !isDefaultAdomain(initial) ? (initial.adomain ?? []).join(', ') : '');
  const [cat, setCat] = useState((initial?.cat ?? []).join(', '));

  const urlProblem = url ? adUrlProblem(url) : null;
  const limits = HOUSE_AD_LIMITS;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          advertiser,
          headline,
          description,
          url,
          paidBy,
          categories: categories
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
          placements,
          startsAt: startsAt ? new Date(startsAt).toISOString() : null,
          endsAt: endsAt ? new Date(endsAt).toISOString() : null,
          weight: Number(weight) || limits.weightDefault,
          enabled,
          adomain: adomain
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
          cat: cat
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          picture,
        });
      }}
    >
      <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">{HOUSE_FORM_RULES}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Advertiser (1 to {limits.advertiser})</span>
          <input type="text" required maxLength={limits.advertiser} value={advertiser} onChange={(e) => setAdvertiser(e.target.value)} className={inputClass} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Paid for by (1 to {limits.paidBy})</span>
          <input type="text" required maxLength={limits.paidBy} value={paidBy} onChange={(e) => setPaidBy(e.target.value)} className={inputClass} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Headline (1 to {limits.headline})</span>
          <input type="text" required maxLength={Math.max(limits.headline, headline.length)} value={headline} onChange={(e) => setHeadline(e.target.value)} className={inputClass} />
          {headline.length > limits.headline ? (
            <span className="mt-1 block text-xs text-destructive">
              {headline.length} characters; shorten it to {limits.headline} to save and to show it again.
            </span>
          ) : null}
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Description (up to {limits.description}, shown on hover)</span>
          <input type="text" maxLength={limits.description} value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1 block text-xs text-muted-foreground">Link (https only)</span>
          <input type="url" required maxLength={limits.url} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/offer" className={inputClass} />
          {urlProblem ? <span className="mt-1 block text-xs text-destructive">{urlProblem}</span> : null}
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Advertiser domains (empty: taken from the link)</span>
          <input
            type="text"
            value={adomain}
            onChange={(e) => setAdomain(e.target.value)}
            placeholder={url && !urlProblem ? defaultAdomain(url).join(', ') : 'example.com'}
            className={inputClass}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Ad Product 2.0 category ids (optional)</span>
          <input type="text" value={cat} onChange={(e) => setCat(e.target.value)} className={inputClass} />
        </label>
        <PictureField
          picture={picture}
          pictureUrl={pictureUrl}
          onChange={(next) => {
            setPicture(next.picture);
            setPictureUrl(next.pictureUrl);
          }}
        />
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Categories (comma separated; empty means any app)</span>
          <input type="text" value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="productivity, creative/image" className={inputClass} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">
            Weight ({limits.weightMin} to {limits.weightMax})
          </span>
          <input type="number" min={limits.weightMin} max={limits.weightMax} value={weight} onChange={(e) => setWeight(e.target.value)} className={inputClass} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Starts (optional)</span>
          <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={inputClass} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Ends (optional)</span>
          <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inputClass} />
        </label>
      </div>
      <div>
        <span className="mb-1 block text-xs text-muted-foreground">Placements (none checked means all)</span>
        <div className="flex flex-wrap gap-3 text-sm">
          {AD_PLACEMENTS.map((p) => (
            <label key={p} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={placements.includes(p)}
                onChange={(e) => setPlacements((list) => (e.target.checked ? [...list, p] : list.filter((x) => x !== p)))}
              />
              {PLACEMENT_LABELS[p]}
            </label>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-1.5 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !!urlProblem} className={primaryButtonClass}>
          <Check className="h-3.5 w-3.5" />
          {initial ? 'Save changes' : 'Add ad'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass}>
          Cancel
        </button>
      </div>
    </form>
  );
}
